import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import { CheckCircle2, ShieldCheck } from "lucide-react";

export default function MerchantPay() {
  const { sid } = useParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/merchant/checkout/${sid}`).then((r) => setSession(r.data)).catch((e)=>setErr(errText(e)));
  }, [sid]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/merchant/checkout/${sid}/pay`);
      setDone(data); toast.success("Payment settled S13");
      if (data.return_url) setTimeout(() => { window.location.href = data.return_url; }, 2000);
    } catch (e) { toast.error(errText(e)); }
    finally { setPaying(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-6 text-white">
      <Toaster theme="dark" richColors position="top-center"/>
      <div className="w-full max-w-md">
        <Link to="/" data-testid="pay-back-link" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#d4af37] to-[#7a5f16] grid place-items-center font-display font-black text-black">★</div>
          <div className="leading-none">
            <div className="font-display font-bold">BlackStar</div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-white/60">CHECKOUT</div>
          </div>
        </Link>

        {err && <div className="panel p-6 text-[#ff3366]" data-testid="pay-error">{err}</div>}

        {session && !done && (
          <div className="panel p-8" data-testid="pay-panel">
            <div className="chip gold mb-4">PAY WITH BLACKSTAR</div>
            <div className="font-display text-4xl">{session.currency} {session.amount.toFixed(2)}</div>
            <div className="text-white/60 text-sm mt-2">to <b className="text-white">{session.merchant_name}</b></div>
            {session.description && <div className="text-white/60 text-sm mt-1">{session.description}</div>}
            <div className="font-mono text-[11px] text-white/40 mt-4">Session {session.id}</div>

            {session.status === "paid" ? (
              <div className="chip green mt-6" data-testid="pay-already">Already settled · {session.transaction_ref}</div>
            ) : user ? (
              <button data-testid="pay-confirm-btn" onClick={pay} disabled={paying} className="btn-primary w-full mt-6">
                {paying ? "Routing S00→S13…" : `Confirm payment as ${user.email}`}
              </button>
            ) : (
              <button data-testid="pay-signin-btn" onClick={() => nav(`/login?return=/pay/${sid}`)} className="btn-primary w-full mt-6">
                Sign in to your vault to pay
              </button>
            )}
            <div className="mt-6 flex items-center gap-2 text-xs text-white/40"><ShieldCheck size={12}/> Secured by S00→S13 state machine · PCI-DSS card vault</div>
          </div>
        )}

        {done && (
          <div className="panel p-8 border-[#39ff14]/40" data-testid="pay-done">
            <CheckCircle2 className="text-[#39ff14]" size={40}/>
            <div className="font-display text-3xl mt-4">Payment settled</div>
            <div className="font-mono text-xs text-white/60 mt-2">Reference {done.transaction_ref}</div>
            {done.return_url && <div className="text-xs text-white/50 mt-4">Redirecting…</div>}
          </div>
        )}

        {!session && !err && <div className="panel p-8 text-white/50" data-testid="pay-loading">Loading checkout…</div>}
      </div>
    </div>
  );
}
